/**
 * Support Requests Real-Time Synchronization Engine
 * Handles local persistence, tab broadcast sync, Cloudflare KV sync, and Google Sheets forwarding
 */

import type { SupportRequest, SupportStatus } from '../types/support';

const STORAGE_KEY = 'ssd_support_requests_list';
const SYNC_TIMESTAMP_KEY = 'ssd_support_requests_last_sync';
const API_ENDPOINT = '/api/support-requests';

// In-memory cache
let cachedRequests: SupportRequest[] | null = null;
let broadcastChannel: BroadcastChannel | null = null;

try {
  if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    broadcastChannel = new BroadcastChannel('ssd_support_requests_sync');
  }
} catch {
  // Ignore in environments without BroadcastChannel
}

/**
 * Get all support requests from local storage
 */
export function getLocalSupportRequests(): SupportRequest[] {
  if (cachedRequests !== null) {
    return cachedRequests;
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        cachedRequests = parsed;
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }

  cachedRequests = [];
  return [];
}

/**
 * Persist requests locally and notify other tabs
 */
function persistRequestsLocally(requests: SupportRequest[]) {
  cachedRequests = requests;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(requests));
    localStorage.setItem(SYNC_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Ignore storage quota errors
  }

  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({
        type: 'SUPPORT_REQUESTS_UPDATED',
        timestamp: Date.now(),
        requests,
      });
    } catch {
      // Ignore broadcast errors
    }
  }
}

/**
 * Create a new support request and sync with server/Google Sheets
 */
export async function createSupportRequest(data: {
  userName: string;
  userEmail: string;
  fileId: string;
  fileName: string;
  fileUrl: string;
  fileCategory: string;
  sourceColumn: 1 | 2 | 3;
  sourceColumnLabel: string;
  issueCategory: string;
  assignedTo?: string;
  assignedEmail?: string;
  description: string;
}): Promise<SupportRequest> {
  const current = getLocalSupportRequests();
  const nowIso = new Date().toISOString();
  const id = 'req-' + Date.now() + '-' + Math.random().toString(36).substring(2, 7);

  const newRequest: SupportRequest = {
    id,
    requestNumber: current.length + 1,
    userName: data.userName.trim() || 'Intranet User',
    userEmail: data.userEmail.trim().toLowerCase(),
    fileId: data.fileId,
    fileName: data.fileName,
    fileUrl: data.fileUrl,
    fileCategory: data.fileCategory || 'General',
    sourceColumn: data.sourceColumn,
    sourceColumnLabel: data.sourceColumnLabel,
    issueCategory: data.issueCategory || 'General Help & Inquiries',
    assignedTo: data.assignedTo || data.issueCategory || 'General',
    assignedEmail: data.assignedEmail || 'adminhelp@ssdspc.com',
    description: data.description.trim(),
    status: 'Pending',
    createdAt: nowIso,
    updatedAt: nowIso,
  };

  const updatedList = [newRequest, ...current];
  persistRequestsLocally(updatedList);

  // Push to server / Google Sheets in background
  pushRequestToServer(newRequest, 'CREATE').catch(console.error);

  return newRequest;
}

/**
 * Update status and optional notes of an existing request
 */
export async function updateSupportRequestStatus(
  id: string,
  newStatus: SupportStatus,
  statusNotes?: string
): Promise<SupportRequest | null> {
  const current = getLocalSupportRequests();
  const index = current.findIndex((r) => r.id === id);
  if (index === -1) return null;

  const nowIso = new Date().toISOString();
  const existing = current[index];
  const updated: SupportRequest = {
    ...existing,
    status: newStatus,
    statusNotes: statusNotes !== undefined ? statusNotes : existing.statusNotes,
    resolvedAt: newStatus === 'Resolved' || newStatus === 'Closed' ? nowIso : existing.resolvedAt,
    updatedAt: nowIso,
  };

  const updatedList = [...current];
  updatedList[index] = updated;
  persistRequestsLocally(updatedList);

  // Push to server / Google Sheets in background
  pushRequestToServer(updated, 'UPDATE').catch(console.error);

  return updated;
}

/**
 * Delete a support request
 */
export async function deleteSupportRequest(id: string): Promise<boolean> {
  const current = getLocalSupportRequests();
  const filtered = current.filter((r) => r.id !== id);
  if (filtered.length === current.length) return false;

  persistRequestsLocally(filtered);

  // Push deletion to server
  try {
    const authRaw = localStorage.getItem('ssd_intranet_auth');
    let idToken = '';
    if (authRaw) {
      try {
        const authData = JSON.parse(authRaw);
        idToken = authData.idToken || '';
      } catch {}
    }

    await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({ action: 'DELETE', requestId: id }),
    });
  } catch {
    // Ignore server error for offline capability
  }

  return true;
}

/**
 * Push request mutation to server and trigger Google Sheet sync
 */
async function pushRequestToServer(request: SupportRequest, action: 'CREATE' | 'UPDATE') {
  try {
    const authRaw = localStorage.getItem('ssd_intranet_auth');
    let idToken = '';
    if (authRaw) {
      try {
        const authData = JSON.parse(authRaw);
        idToken = authData.idToken || '';
      } catch {}
    }

    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({ action, request }),
    });

    if (response.ok) {
      const data = await response.json();
      if (data && Array.isArray(data.requests)) {
        persistRequestsLocally(data.requests);
      }
    }
  } catch (err) {
    console.warn('[SupportSync] Failed to push to server, saved locally:', err);
  }
}

/**
 * Fetch latest requests from Cloudflare KV / Google Sheet
 */
export async function syncSupportRequestsWithServer(): Promise<boolean> {
  try {
    const authRaw = localStorage.getItem('ssd_intranet_auth');
    let idToken = '';
    if (authRaw) {
      try {
        const authData = JSON.parse(authRaw);
        idToken = authData.idToken || '';
      } catch {}
    }

    const response = await fetch(API_ENDPOINT, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data && Array.isArray(data.requests)) {
        // Merge server requests with local requests
        const local = getLocalSupportRequests();
        const serverMap = new Map<string, SupportRequest>();
        data.requests.forEach((r: SupportRequest) => {
          if (r && r.id) serverMap.set(r.id, r);
        });

        // Ensure newly created offline local requests are preserved
        local.forEach((r) => {
          if (!serverMap.has(r.id)) {
            serverMap.set(r.id, r);
          }
        });

        const merged = Array.from(serverMap.values()).sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        persistRequestsLocally(merged);
        return true;
      }
    }
  } catch {
    // Return false on offline / network issues
  }
  return false;
}

/**
 * Subscribe to support request changes across tabs
 */
export function onSupportRequestsChange(callback: (requests: SupportRequest[]) => void): () => void {
  if (!broadcastChannel) {
    return () => {};
  }

  const handler = (event: MessageEvent) => {
    if (event.data && event.data.type === 'SUPPORT_REQUESTS_UPDATED') {
      cachedRequests = event.data.requests;
      callback(event.data.requests);
    }
  };

  broadcastChannel.addEventListener('message', handler);

  return () => {
    if (broadcastChannel) {
      broadcastChannel.removeEventListener('message', handler);
    }
  };
}
